/**
 * The account menu, and the only sign-out in the app.
 *
 * It was missing entirely, which matters more here than in most products: this platform's
 * own argument for forcing a password change is that a shared credential makes the audit
 * trail meaningless. A console that shows no signed-in identity and offers no way to leave
 * invites exactly the shared-session habit that argument exists to prevent.
 *
 * So the menu states WHO and AS WHAT on the way to the sign-out, not just the button.
 * Role is included because it is why an action is missing — an auditor who cannot see the
 * Integrate button has no other way to learn that is deliberate.
 *
 * AND THE WAY TO APPEARANCE (prototype v2, 2026-11-02; Asif's review, point 5): light or dark, the
 * look and the zoom left the header bar, and this menu is where a person reaches them: its Appearance
 * row opens Settings › Appearance (prototype v2's Settings, 2026-11-02), as the prototype's does.
 * Above it, "Your account" (Settings › Account) and "Settings" (its default section), as the
 * reference design's menu has them (2026-11-03). Each opens one history entry, so Back returns.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth-context';
import { roleLabel, roleScope, useMe } from '@/lib/api/me';
import { Icon } from './Icon';

export function AccountMenu({
  onOpenAccount,
  onOpenSettings,
  onOpenAppearance,
}: {
  /** Open Settings › Account. Absent, the row is not drawn. */
  onOpenAccount?: (() => void) | undefined;
  /** Open Settings at its default section. Absent, the row is not drawn. */
  onOpenSettings?: (() => void) | undefined;
  /** Open Settings at Appearance. Absent, the row is not drawn. */
  onOpenAppearance?: (() => void) | undefined;
} = {}): ReactNode {
  const { user, signOut } = useAuth();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  /* Dismiss on outside click and on Escape — a menu that only closes by re-clicking its
     own trigger is the classic way to trap someone behind an overlay. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const email = me.data?.user.email ?? user?.email ?? 'unknown';
  const name = me.data?.user.fullName;
  const role = me.data?.user.role;
  const org = me.data?.organisation.name;
  const initial = (name ?? email).trim().charAt(0).toUpperCase() || '?';

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        /* Never shrinks and never leaves the screen (`.lm-topbar-tools > *`, app.css): since v2 it is
           the only way to Appearance and Sign out. On a phone (under 640 px) the avatar alone, 32 px
           square like the bar's other controls; the caret returns from 640 px. */
        className="lm-hov flex h-[var(--header-ctl-h)] items-center gap-[4px] rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--card)] pl-[4px] pr-[4px] aria-expanded:border-[var(--ring)] aria-expanded:bg-[var(--muted)] sm:gap-[7px] sm:pr-[8px]"
        title={`Signed in as ${email}`}
      >
        <span
          className="lm-num"
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--brand)',
            color: 'var(--brand-fg)',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {initial}
        </span>
        <span className="hidden sm:flex" aria-hidden>
          <Icon name={open ? 'ic-chevup' : 'ic-chevdown'} size={13} />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="lm-card"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            minWidth: 268,
            zIndex: 50,
            padding: 0,
            overflow: 'hidden',
          }}
        >
          {/* -------------------------------------------------------- identity */}
          <div className="border-b p-[12px_14px]" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[13px] font-bold" style={{ wordBreak: 'break-all' }}>
              {name ?? email}
            </div>
            {name && (
              <div className="text-[11.5px]" style={{ color: 'var(--muted-fg)', wordBreak: 'break-all' }}>
                {email}
              </div>
            )}
            {org && (
              <div className="mt-[6px] flex items-center gap-[6px] text-[11.5px]" style={{ color: 'var(--muted-fg)' }}>
                <Icon name="ic-building" size={12} />
                {org}
              </div>
            )}
          </div>

          {/* ------------------------------------------------------------ role */}
          {role && (
            <div className="border-b p-[10px_14px]" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between gap-[10px]">
                <span
                  className="text-[12px] font-semibold"
                  style={{ color: 'var(--muted-fg)' }}
                >
                  Role
                </span>
                {/* A word, not a capsule (v2: plain text before capsules; pills are for states). */}
                <span className="text-[12px] font-semibold text-[var(--fg)]">{roleLabel(role)}</span>
              </div>
              {/* Why a button is missing. Without this the UI just looks broken to a
                  read-only user. */}
              <p className="mt-[5px] text-[11.5px] leading-[1.45]" style={{ color: 'var(--muted-fg)' }}>
                {roleScope(role)}
              </p>
            </div>
          )}

          {/* ---------------------------------------------- account, settings */}
          {onOpenAccount && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenAccount();
              }}
              className="lm-hov flex w-full items-center gap-[9px] border-b border-[var(--border)] p-[11px_14px] text-left text-[13px] font-semibold text-[var(--fg)]"
            >
              <Icon name="ic-gauge" size={15} />
              Your account
            </button>
          )}

          {onOpenSettings && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenSettings();
              }}
              className="lm-hov flex w-full items-center gap-[9px] border-b border-[var(--border)] p-[11px_14px] text-left text-[13px] font-semibold text-[var(--fg)]"
            >
              <Icon name="ic-sliders" size={15} />
              Settings
            </button>
          )}

          {/* ------------------------------------------------------ appearance */}
          {onOpenAppearance && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenAppearance();
              }}
              className="lm-hov flex w-full items-center gap-[9px] border-b border-[var(--border)] p-[11px_14px] text-left text-[13px] font-semibold text-[var(--fg)]"
            >
              <Icon name="ic-sliders" size={15} />
              Appearance
            </button>
          )}

          {/* -------------------------------------------------------- sign out */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setSigningOut(true);
              void signOut();
            }}
            disabled={signingOut}
            className="lm-hov flex w-full items-center gap-[9px] p-[11px_14px] text-left text-[13px] font-semibold"
            style={{ color: 'var(--danger)' }}
          >
            <Icon name="ic-logout" size={15} />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
